import { useState } from 'react';
import { Button, Field, Modal, Pill, TextInput } from '@salonomy/ui';
import { useVoucherBalanceByCode } from '../../data/vouchers.js';
import { money } from '../../lib/format.js';
import './till.css';

/**
 * WS9 §2 — staff/till voucher BALANCE lookup (surface d). Enter or scan a code → show the server-derived
 * balance. Read-only: this never redeems and never touches money — it DISPLAYS the API's `balancePence`
 * (which is the signed sum of the ledger, never a stored number), so what's shown is always the ledger
 * truth. Redemption lives in VoucherRedeemModal; this is the "what's left on this card?" question.
 *
 * The lookup query is gated on a submitted code (not keystrokes) so an empty/typing field doesn't probe
 * the endpoint; the hook returns null on a 404 so an unknown/void code shows cleanly rather than as an
 * error.
 */
export function VoucherBalanceModal({ onClose }: { onClose: () => void }) {
  const [draft, setDraft] = useState('');
  const [code, setCode] = useState<string | undefined>(undefined);
  const { data: balance, isFetching } = useVoucherBalanceByCode(code);

  function lookup() {
    const next = draft.trim();
    if (next) setCode(next);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Voucher balance"
      width={420}
      footer={
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Field
        label="Voucher / gift-card code"
        hint="Type or scan the code from the card or receipt."
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <TextInput
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') lookup();
            }}
            placeholder="e.g. GC-7Q4M-2KX9"
            style={{ textTransform: 'uppercase' }}
          />
          <Button onClick={lookup} disabled={!draft.trim()}>
            Look up
          </Button>
        </div>
      </Field>

      {code && !isFetching && balance === null ? (
        <div className="muted" style={{ marginTop: 12, fontSize: 13.5 }}>
          No active voucher matches that code. Check the code and try again.
        </div>
      ) : null}

      {balance ? (
        <div className="till-voucher__found" style={{ marginTop: 12 }}>
          <div className="till__line till__line--total">
            <span>Balance</span>
            <span className="mono">{money(balance.balancePence)}</span>
          </div>
          <div className="till__line muted">
            <span>Issued value</span>
            <span className="mono">{money(balance.originalAmountPence)}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Pill
              tone={
                balance.status === 'active'
                  ? 'success'
                  : balance.status === 'expired'
                    ? 'neutral'
                    : 'danger'
              }
            >
              {balance.status}
            </Pill>
            <Pill tone="neutral">{balance.kind === 'gift_card' ? 'Gift card' : 'Voucher'}</Pill>
            <Pill tone="neutral">Expires {balance.expiresAt.slice(0, 10)}</Pill>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
